import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Palette, Frame, Star, Check, Lock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Rarity colors
const RARITY_COLORS = {
  common: "from-gray-400 to-gray-500",
  rare: "from-blue-400 to-blue-600",
  epic: "from-purple-400 to-purple-600",
  legendary: "from-yellow-400 to-orange-500"
};

const RARITY_BORDER = {
  common: "border-gray-400",
  rare: "border-blue-400",
  epic: "border-purple-400",
  legendary: "border-yellow-400"
};

const TAB_CONFIG = [
  { id: "skins", label: "Skins", icon: Sparkles },
  { id: "effects", label: "Effects", icon: Star },
  { id: "themes", label: "Themes", icon: Palette },
  { id: "frames", label: "Frames", icon: Frame }
];

export function CosmeticsModal({ 
  isOpen, 
  onClose, 
  unlockedSkins = [],
  unlockedEffects = [],
  unlockedThemes = [],
  unlockedFrames = [],
  equippedSkin,
  equippedEffect,
  equippedTheme,
  equippedFrame,
  onEquip
}) {
  const [activeTab, setActiveTab] = useState("skins");
  const [availableCosmetics, setAvailableCosmetics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [equipping, setEquipping] = useState(false);

  // Fetch available cosmetics on mount
  useEffect(() => {
    if (isOpen) {
      fetchAvailableCosmetics();
    }
  }, [isOpen]);

  const fetchAvailableCosmetics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cosmetics/available`);
      if (response.ok) {
        const data = await response.json();
        setAvailableCosmetics(data);
      }
    } catch (error) {
      console.error('Failed to fetch cosmetics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEquip = async (type, id) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setEquipping(true);
    try {
      const response = await fetch(`${API_URL}/api/cosmetics/equip`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          cosmetic_type: type.slice(0, -1), // Remove 's' from 'skins' -> 'skin'
          cosmetic_id: id 
        })
      });

      if (response.ok) {
        onEquip?.(type, id);
      }
    } catch (error) {
      console.error('Failed to equip cosmetic:', error);
    } finally {
      setEquipping(false);
    }
  };

  const getUnlockedItems = (type) => {
    switch (type) {
      case "skins": return unlockedSkins;
      case "effects": return unlockedEffects;
      case "themes": return unlockedThemes;
      case "frames": return unlockedFrames;
      default: return [];
    }
  };

  const getEquippedItem = (type) => {
    switch (type) {
      case "skins": return equippedSkin;
      case "effects": return equippedEffect;
      case "themes": return equippedTheme;
      case "frames": return equippedFrame;
      default: return null;
    }
  };

  if (!isOpen) return null;

  const currentItems = availableCosmetics?.[activeTab] || {};
  const unlockedItems = getUnlockedItems(activeTab);
  const equippedItem = getEquippedItem(activeTab);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <Card className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800 border-2 border-purple-500/50 shadow-2xl">
        {/* Header */}
        <div className="relative p-4 bg-gradient-to-r from-purple-600 to-pink-600">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Cosmetics</h2>
              <p className="text-purple-200 text-sm">Customize your game experience</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {TAB_CONFIG.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 transition-all ${
                  isActive 
                    ? "bg-purple-500/30 text-purple-300 border-b-2 border-purple-400" 
                    : "text-gray-400 hover:text-gray-300 hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {/* Unequip option */}
              <button
                onClick={() => handleEquip(activeTab, null)}
                disabled={equipping || !equippedItem}
                className={`p-4 rounded-xl border-2 transition-all ${
                  !equippedItem
                    ? "border-green-500 bg-green-500/20"
                    : "border-gray-600 bg-gray-800/50 hover:border-gray-500"
                }`}
              >
                <div className="text-3xl mb-2">🚫</div>
                <div className="text-sm font-medium text-gray-300">None</div>
                <div className="text-xs text-gray-500">Default look</div>
                {!equippedItem && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-green-400 text-xs">
                    <Check className="w-3 h-3" /> Equipped
                  </div>
                )}
              </button>

              {/* Cosmetic items */}
              {Object.entries(currentItems).map(([id, item]) => {
                const isUnlocked = unlockedItems.includes(id);
                const isEquipped = equippedItem === id;
                const rarityColor = RARITY_COLORS[item.rarity] || RARITY_COLORS.common;
                const rarityBorder = RARITY_BORDER[item.rarity] || RARITY_BORDER.common;

                return (
                  <button
                    key={id}
                    onClick={() => isUnlocked && handleEquip(activeTab, id)}
                    disabled={equipping || !isUnlocked}
                    className={`relative p-4 rounded-xl border-2 transition-all ${
                      isEquipped
                        ? "border-green-500 bg-green-500/20"
                        : isUnlocked
                          ? `${rarityBorder} bg-slate-800/50 hover:bg-slate-700/50`
                          : "border-gray-700 bg-gray-900/50 opacity-60"
                    }`}
                  >
                    {/* Rarity glow for unlocked items */}
                    {isUnlocked && (
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${rarityColor} opacity-10`} />
                    )}
                    
                    {/* Lock overlay */}
                    {!isUnlocked && (
                      <div className="absolute inset-0 rounded-xl bg-black/50 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-gray-500" />
                      </div>
                    )}

                    <div className="relative">
                      <div className="text-3xl mb-2">
                        {item.preview || item.emoji || "✨"}
                      </div>
                      <div className="text-sm font-medium text-white truncate">
                        {item.name}
                      </div>
                      <div className={`text-xs capitalize mt-1 bg-gradient-to-r ${rarityColor} bg-clip-text text-transparent font-semibold`}>
                        {item.rarity}
                      </div>
                      
                      {isEquipped && (
                        <div className="mt-2 flex items-center justify-center gap-1 text-green-400 text-xs">
                          <Check className="w-3 h-3" /> Equipped
                        </div>
                      )}
                      
                      {!isUnlocked && (
                        <div className="mt-2 text-xs text-gray-500 truncate">
                          {item.source}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-black/20 border-t border-white/10">
          <p className="text-purple-200 text-xs text-center">
            Unlock more cosmetics through the Battle Pass! 🎫
          </p>
        </div>
      </Card>
    </div>
  );
}
